import type { Role } from '@/features/auth/api';
import { apiFetch } from '@/lib/api/http';

/**
 * 期マスタ（指示書 §10）。
 * マスタ値の削除は物理削除せず「無効化」。使用中件数（member_count）を表示する。
 */
export interface Cohort {
  id: string;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  member_count: number;
}

export function fetchCohorts(): Promise<Cohort[]> {
  return apiFetch<Cohort[]>('/masters/cohorts');
}

export function createCohort(input: { name: string; sort_order?: number }): Promise<Cohort> {
  return apiFetch<Cohort>('/masters/cohorts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function updateCohort(
  id: string,
  input: { name?: string; sort_order?: number },
): Promise<Cohort> {
  return apiFetch<Cohort>(`/masters/cohorts/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function setCohortActive(id: string, isActive: boolean): Promise<Cohort> {
  return apiFetch<Cohort>(`/masters/cohorts/${id}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_active: isActive }),
  });
}

/**
 * コースマスタ（期と並ぶもう一方の軸 / D-114）。
 * 期と同じ扱い。削除は物理削除せず「無効化」。
 */
export interface Course {
  id: string;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  member_count: number;
}

export function fetchCourses(): Promise<Course[]> {
  return apiFetch<Course[]>('/masters/courses');
}

export function createCourse(input: { name: string; sort_order?: number }): Promise<Course> {
  return apiFetch<Course>('/masters/courses', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function updateCourse(
  id: string,
  input: { name?: string; sort_order?: number },
): Promise<Course> {
  return apiFetch<Course>(`/masters/courses/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function setCourseActive(id: string, isActive: boolean): Promise<Course> {
  return apiFetch<Course>(`/masters/courses/${id}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_active: isActive }),
  });
}

/**
 * イベント種別マスタ（標準6種＋カスタム追加可 / 指示書 §4・§10）。
 */
export interface EventType {
  id: string;
  code: string;
  name: string;
  /** 1対1の面談のとき、相手のロール（サクセス面談=success / 1on1=director）。null は通常イベント（D-123） */
  counterpart_role: Role | null;
  sort_order: number;
  is_active: boolean;
  event_count: number;
}

export function fetchEventTypes(): Promise<EventType[]> {
  return apiFetch<EventType[]>('/masters/event-types');
}

export function createEventType(input: {
  name: string;
  counterpart_role?: Role | null;
  sort_order?: number;
}): Promise<EventType> {
  return apiFetch<EventType>('/masters/event-types', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function updateEventType(
  id: string,
  input: {
    name?: string;
    counterpart_role?: Role | null;
    sort_order?: number;
  },
): Promise<EventType> {
  return apiFetch<EventType>(`/masters/event-types/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function setEventTypeActive(id: string, isActive: boolean): Promise<EventType> {
  return apiFetch<EventType>(`/masters/event-types/${id}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_active: isActive }),
  });
}
